import { Component, Input, OnChanges } from '@angular/core';
import { CeoV2Auth } from 'src/app/interfaces/ceo-v2.interface';
import { CeoV2Service } from '../ceo-v2.service';

type Kind = 'agentCostMarkupPct' | 'aiIntegrationsMarkupPct' | 'infraMarkupPct';
interface PriceRow { markupPct: number; baseCredits: number; markedCredits: number }
export interface CostMarkupsViewI {
  accountUserId: string;
  markups: Record<Kind, number>;
  updatedAt: string | null;
  updatedBy: string | null;
  effective: { costUsd: number; prompt: PriceRow; integrations: PriceRow; infrastructure: PriceRow };
}

/**
 * A6.44: the account's v2 cost markups on the CEO user panel — agent runs, built-in (AI) integrations, infrastructure,
 * each 0–500 % on the cost before the credit calculation — with the credits $1 of cost costs this account next to an
 * unmarked one. A write takes the v2 second factor and is audited; the customer never sees any of it.
 */
@Component({
  selector: 'app-ceo-v2-markups',
  templateUrl: './ceo-v2-markups.component.html',
})
export class CeoV2MarkupsComponent implements OnChanges {
  @Input() accountUserId: string | null = null;
  view: CostMarkupsViewI | null = null;
  draft: Record<Kind, number | ''> = { agentCostMarkupPct: 0, aiIntegrationsMarkupPct: 0, infraMarkupPct: 0 };
  error = '';
  readonly rows: { kind: Kind; label: string; price: 'prompt' | 'integrations' | 'infrastructure' }[] = [
    { kind: 'agentCostMarkupPct', label: 'Agent runs (prompts)', price: 'prompt' },
    { kind: 'aiIntegrationsMarkupPct', label: 'AI & built-in integrations', price: 'integrations' },
    { kind: 'infraMarkupPct', label: 'Database & Workers (infra)', price: 'infrastructure' },
  ];

  constructor(private v2: CeoV2Service) {}

  ngOnChanges(): void {
    void this.load();
  }

  private path(): string {
    return `v2/accounts/${encodeURIComponent(this.accountUserId ?? '')}/markups`;
  }

  async load(): Promise<void> {
    if (!this.accountUserId) return;
    const r = await this.v2.get<CostMarkupsViewI>(this.path());
    this.view = r.data;
    this.error = r.error ?? '';
    if (r.data) this.draft = { ...r.data.markups };
  }

  save = async (auth: CeoV2Auth): Promise<string | null> => {
    const markups = {} as Record<Kind, number>;
    for (const { kind } of this.rows) markups[kind] = Number(this.draft[kind] || 0);
    if (this.rows.some(({ kind }) => !(markups[kind] >= 0 && markups[kind] <= 500))) return 'Each markup is a percentage from 0 to 500';
    const r = await this.v2.write<CostMarkupsViewI>('PUT', this.path(), { ...auth, markups });
    if (!r.error && r.data) {
      this.view = r.data;
      this.draft = { ...r.data.markups };
    }
    return r.error;
  };
}
